import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Headphones, CheckCircle2, Archive } from 'lucide-react';

/**
 * QualityMetrics - reliability / quality metrics band (bg-bg-soft).
 *
 * Distinct from TrustIndicators (which shows VOLUME stats - docs/day, partners,
 * web forms). This section speaks to reliability: uptime, human support,
 * downtime, archive restore.
 *
 * ⚠️ OPS CONFIRMATION REQUIRED - every figure below is sourced from existing
 * copy in src/config/siteContent.js (EDI_PROCESS_STEPS "99.9% uptime",
 * VALUE_PROPS "24/7 Support" + "36 months archiving", and the FTE Automotive
 * case study "zero documented downtime"). These are REPRESENTATIVE marketing
 * figures, NOT contractual SLAs. Confirm the current numbers with iConnect
 * operations before publishing, and update the metrics array accordingly.
 */

// Sourced from siteContent.js - do not invent new SLA numbers here.
const QUALITY_METRICS = [
  {
    icon: ShieldCheck,
    value: '99.9%+',
    label: 'Uptime',
    sublabel: 'VAN reliability',
  },
  {
    icon: Headphones,
    value: '24/7',
    label: 'Human Support',
    sublabel: 'Real engineers on the phone, not bots',
  },
  {
    icon: CheckCircle2,
    value: '0',
    label: 'Documented Downtime',
    sublabel: 'Per FTE Automotive VAN migration',
  },
  {
    icon: Archive,
    value: '36 mo',
    label: 'Instant Archive',
    sublabel: 'Self-service restore on demand',
  },
];

const QualityMetrics = () => {
  return (
    <section className="relative py-24 lg:py-32 bg-bg-canvas overflow-hidden">
      {/* Subtle radial sky glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 right-1/4 w-96 h-96 rounded-full bg-accent-soft/40 blur-3xl" />
        <div className="absolute -bottom-32 left-1/4 w-96 h-96 rounded-full bg-accent-soft/40 blur-3xl" />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0.5, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14 max-w-3xl xl:max-w-none mx-auto"
        >
          <p className="eyebrow">Quality &amp; Reliability</p>
          {/* This heading is long; tighter tracking + xl:whitespace-nowrap keep it
              on one line at full size on laptops/desktops. Wraps below xl on phones. */}
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-4 tracking-[-0.04em] text-balance xl:whitespace-nowrap">
            <span className="text-gradient-sky">Reliability</span>{' '}
            built for production environments
          </h2>
          <p className="text-lg text-text-secondary text-pretty xl:whitespace-nowrap">
            Volume is one thing. Dependability is another. These are the numbers
            that keep just-in-time manufacturing lines moving.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {QUALITY_METRICS.map((metric, idx) => {
            const Icon = metric.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="text-center group"
              >
                <div className="w-11 h-11 rounded-lg bg-gradient-sky shadow-glow-sky flex items-center justify-center mx-auto mb-4">
                  <Icon size={20} className="text-white" />
                </div>
                <div className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-gradient-sky mb-2 leading-none tracking-tight">
                  {metric.value}
                </div>
                <div className="text-text-primary font-semibold mb-1 text-base lg:text-lg">
                  {metric.label}
                </div>
                <div className="text-sm text-text-muted">{metric.sublabel}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QualityMetrics;
